import { lazy, Suspense, useEffect, useState, type MutableRefObject } from 'react';
import { detectCapability, type Capability } from './capability';
import { StaticFallback } from './StaticFallback';

const AscentScene = lazy(() => import('./AscentScene'));

export interface AscentCanvasProps {
  scrollRef: MutableRefObject<number>;
  /** 'hero' = landing page's full scroll-driven narrative. 'ambient' = a
   * smaller, dimmer, always-assembled presence for every other page. */
  layoutMode?: 'hero' | 'ambient';
}

/**
 * Persistent, fixed, z-index:0 WebGL layer for "The Ascent" (replaces the
 * Earth). This wrapper itself never imports three/@react-three-fiber — that
 * whole stack is behind a React.lazy() boundary (see AscentScene.tsx) so
 * low-end/no-WebGL/prefers-reduced-motion visitors never pay for downloading
 * it; they get the static CSS fallback and nothing else.
 */
export function AscentCanvas({ scrollRef, layoutMode = 'hero' }: AscentCanvasProps) {
  const [capability, setCapability] = useState<Capability | null>(null);
  const [inFrame, setInFrame] = useState(!document.hidden);

  useEffect(() => {
    let cancelled = false;
    detectCapability()
      .then((c) => {
        if (!cancelled) setCapability(c);
      })
      .catch(() => {
        if (!cancelled) setCapability({ tier: 'low', reducedMotion: false, webgl: false, isMobile: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // The only signal that should ever pause rendering: real tab
    // visibility changes. (An IntersectionObserver on the wrapper used to
    // also gate this, but the wrapper is `fixed inset-0` — permanently
    // covering the viewport — so it never re-fires from scrolling, and a
    // single stale `document.hidden` snapshot at mount could latch
    // `inFrame` to false forever, freezing the whole scene with no way to
    // recover. Rely solely on the event that's actually reactive.)
    const onVisibility = () => setInFrame(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Still probing capability (bounded to ~1s even in a throttled/backgrounded
  // tab — see capability.ts) — stay transparent, the ACT 0 loader covers the
  // screen for this window anyway.
  if (!capability) return null;

  if (capability.tier === 'low') return <StaticFallback />;

  return (
    <div className="fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <Suspense fallback={null}>
        <AscentScene
          scrollRef={scrollRef}
          tier={capability.tier}
          isMobile={capability.isMobile}
          frameloop={inFrame ? 'always' : 'never'}
          layoutMode={layoutMode}
        />
      </Suspense>
    </div>
  );
}
